import { formatDate } from '@angular/common';

export type DateFilterValue = Date | (Date | undefined | null)[] | undefined | null;

export function setMinTime(element: Date | undefined): Date | undefined {
  if (element) {
    return new Date(element.getFullYear(), element.getMonth(), element.getDate(), 0, 0, 0);
  }

  return element;
}

export function formatDateWithTime(element: Date | undefined): string {
  return formatDateBase(element, 'dd.MM.yyyy HH:mm:ss');
}

export function formatDateOnly(element: Date | string | undefined): string {
  return formatDateBase(element, 'dd.MM.yyyy');
}

export function formatDateYYYYMMDD(element: Date | undefined): string {
  return formatDateBase(element, 'yyyy-MM-dd');
}

function formatDateBase(element: Date | string | undefined, format: string): string {
  if (element) {
    return formatDate(element, format, 'de-CH');
  } else {
    return '';
  }
}

export function formatMonthName(month: number, locale: string) {
  return new Date(2000, month - 1).toLocaleString(locale, { month: 'long' });
}

const MIN_VALID_YEAR = 1900;
// lower bound for datepickers, matches the default of isValidDate
export const MIN_VALID_DATE = new Date(MIN_VALID_YEAR, 0, 1);

export const isValidDate = (date: unknown, minYear = MIN_VALID_YEAR): date is Date => {
  return date instanceof Date && !Number.isNaN(date.getTime()) && date.getFullYear() >= minYear;
};

const isNullOrUndefined = (val: unknown): val is null | undefined => {
  return val === null || val === undefined;
};

const isValidSingleValue = (element: unknown): boolean => {
  return isNullOrUndefined(element) || isValidDate(element);
};

export function isValidDateOrRange(
  value: unknown,
  options: {
    allowSingleUndefinedArray?: boolean;
  } = {},
): boolean {
  const { allowSingleUndefinedArray = false } = options;

  if (!Array.isArray(value)) {
    return isValidSingleValue(value);
  }

  if (value.length === 0 || value.length > 2) {
    return false;
  }

  if (!value.every(isValidSingleValue)) {
    return false;
  }

  if (value.length === 1) {
    const [element] = value;
    if (isNullOrUndefined(element)) {
      return allowSingleUndefinedArray;
    }
    return true;
  }

  const [start, end] = value;
  const bothEmpty = isNullOrUndefined(start) && isNullOrUndefined(end);
  const bothDates = isValidDate(start) && isValidDate(end) && start <= end;
  // Also allow [Date, null/undefined]: a partial range from bsDaterangepicker when only the
  // start date has been entered. Treated as a single-date filter (from = until = start).
  const startOnly = isValidDate(start) && isNullOrUndefined(end);

  return bothEmpty || bothDates || startOnly;
}
